import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Anchor, Box, Group, Paper, Stack, Text, TextInput, UnstyledButton } from "@mantine/core";
import { useDebouncedValue, useHover } from "@mantine/hooks";
import {
  IconClock,
  IconDownload,
  IconMapPin,
  IconMessage,
  IconStopwatch,
  IconUser,
} from "@tabler/icons-react";
import { useMe, useSegments, usePublicSettings } from "../api/queries";
import type { Segment } from "../api/types";
import { nativeRuntime } from "../platform/native";
import { DownloadButton } from "../downloads/DownloadButton";
import { handleNativeLink } from "../platform/nativeLinks";
import { formatDate, formatDuration, formatRelative, formatTime } from "../time/format";
import { useNow } from "../time/useNow";
import { useZone } from "../time/useZone";
import { usePlayer } from "../state/player";
import { useListQuery, usePrefs, segmentQuery, type Filters } from "../state/prefs";
import { Artwork } from "../ui/Artwork";
import { alpha, token } from "../ui/tokens";
import { Chip } from "../ui/Chip";
import { DateMenu, FilterMenu } from "../ui/FilterMenu";
import { useLayout } from "../ui/useLayout";
import { Badge, BadgeLegend, type BadgeKey } from "../ui/badges";
import { FeedbackLine } from "../ui/FeedbackLine";

const PAGE_SIZE = 50;

/** Typing a letter should not fire a query per keystroke; the search is server-side. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * How often the rows re-read the clock for their "2h 14m ago" column.
 *
 * A minute, because that is the finest thing `formatRelative` ever prints: a faster tick
 * would re-render fifty rows to draw the same words back.
 */
const RELATIVE_TICK_MS = 60_000;

export function StreamList() {
  const [rawQuery, setRawQuery] = useState("");
  // Trailing edge, and it cancels on unmount -- the local hook this replaced did the first
  // but not the second.
  const [query] = useDebouncedValue(rawQuery, SEARCH_DEBOUNCE_MS);
  const filters = usePrefs((s) => s.listFilters);
  const setFilters = usePrefs((s) => s.setListFilters);
  const [page, setPage] = useState(0);
  const zone = useZone();
  const now = useNow(RELATIVE_TICK_MS);
  const { t } = useTranslation();
  const layout = useLayout();

  // A new search or a new filter starts at the top; keeping the offset would show page three
  // of results that may only have one page. Done in the two events that narrow the listing
  // rather than in an effect watching them: an effect lands a render late, so the request for
  // page three of the old results goes out before the reset does.
  const changeFilters = (next: Filters) => {
    setFilters(next);
    setPage(0);
  };

  // The debounced term is published to the player: its queue is this listing, so the search
  // is part of the filter it opens on.
  useEffect(() => {
    useListQuery.getState().setQuery(query);
  }, [query, filters]);

  const { data, isFetching, isPlaceholderData } = useSegments({
    ...segmentQuery(filters, zone.name),
    q: query || undefined,
    // A show cut in half by a dropped connection is one show here: only its first section is
    // listed, and clicking that is what plays the whole of it. The sections are still their
    // own blocks on the timeline, and the jukebox track between them is still in this list.
    joined: false,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const selectedId = usePlayer((s) => s.selectedId);
  // Read once here rather than per row: the query is cached, but fifty subscriptions to it
  // is fifty re-renders for one boolean.
  const settings = usePublicSettings().data;
  const chatEnabled = settings?.chat_enabled ?? false;
  // Passed down for the same reason: it is one string for the whole page, and a row that
  // subscribed for itself would re-render fifty times to learn it.
  const defaultCover = settings?.default_cover_url ?? null;
  // Signed out there is no tracker at all, so the key must not name its two marks: a flag
  // named but never drawn reads as one the viewer has missed.
  const signedIn = !!useMe().data;
  const flags: readonly BadgeKey[] = signedIn
    ? ["track", "live", "heard", "partial", "starred", "expired"]
    : ["track", "live", "starred", "expired"];
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <Paper
      component="section"
      p="sm"
      aria-label={t("streamList.sectionLabel")}
      // The `Grid.Col` around this is a flex column of definite height, so one `flex: 1`
      // replaces both the phone stack's `flex: 1` and the desktop cell's stretch.
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Group component="header" gap={8} mb={8} wrap="nowrap">
        <Text component="h2" size="xs" fw={600} lts="0.35px" tt="uppercase" c="dimmed">
          {t("streamList.heading")}
        </Text>
      </Group>
      {/* 8px to match the review queue's header row: the same two triggers sit in both, and
          12px left the pair floating away from the search field it narrows. The row keeps that
          gap on a phone; only the space under it gives way there. */}
      <Group gap={8} mb={layout.phone ? 8 : 10} wrap="nowrap">
        <TextInput
          type="search"
          flex={1}
          miw={0}
          value={rawQuery}
          placeholder={t("streamList.searchPlaceholder")}
          aria-label={t("streamList.searchLabel")}
          onChange={(event) => {
            setRawQuery(event.currentTarget.value);
            setPage(0);
          }}
        />
        <DateMenu value={filters} onChange={changeFilters} />
        <FilterMenu value={filters} onChange={changeFilters} tracker={signedIn} />
      </Group>

      {/* The result count and, hard right on the same line, the key for the letters the rows
          wear. It sits here rather than up by the label because this is the last line before
          the rows themselves -- the key explains what is directly under it, and up in the
          corner it was answering for a card the reader had already scrolled past.

          Gone in landscape: with the header, the player and the canvas all sharing ~390px of
          height, the count is one of the two things on the page no control needs. */}
      <Group
        wrap="nowrap"
        justify="space-between"
        gap={8}
        mb={6}
        fz={12}
        c="dimmed"
        display={layout.landscape ? "none" : undefined}
      >
        {isFetching && (!data || isPlaceholderData)
          ? t("streamList.searching")
          : t("streamList.results", { count: total })}
        <BadgeLegend of={flags} />
      </Group>

      {/* The list is the only thing on the page that scrolls; the timeline and the player
          stay put so playback controls never scroll out of reach. */}
      <Box
        component="ul"
        m={0}
        p={0}
        flex={1}
        mih={0}
        bg="transparent"
        style={{
          listStyle: "none",
          overflowY: "auto",
          // Without it, flicking past the end rubber-bands the document and arms
          // pull-to-refresh.
          overscrollBehavior: "contain",
        }}
      >
        {items.map((segment, index) => (
          <Row
            key={segment.id}
            segment={segment}
            first={index === 0}
            selected={segment.id === selectedId}
            chat={chatEnabled}
            signedIn={signedIn}
            defaultCover={defaultCover}
            now={now}
          />
        ))}
        {!items.length && !isFetching && (
          <Text component="li" p={24} ta="center" c="dimmed">
            {t("streamList.empty")}
          </Text>
        )}
      </Box>

      {total > PAGE_SIZE && (
        <Group gap={12} justify="center" pt={10} c="dimmed" wrap="nowrap">
          <Chip disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            {t("streamList.newer")}
          </Chip>
          <Text component="span" inherit>
            {page + 1} / {Math.ceil(total / PAGE_SIZE)}
          </Text>
          <Chip disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
            {t("streamList.older")}
          </Chip>
        </Group>
      )}
    </Paper>
  );
}

/** City, state and country as one line, skipping whichever parts the title did not carry. */
function place(segment: Segment): string | null {
  return [segment.city, segment.region, segment.country].filter(Boolean).join(", ") || null;
}

/**
 * The play overlay swallows clicks for everything else in the row; this one is a real link, so
 * it opts back in.
 *
 * The dim-until-pointed-at treatment is a hover on an inline 15px glyph, and the two Mantine
 * components that own a hover (`ActionIcon`, `Button`) both bring a fixed square box it
 * cannot take beside a title. So the state is read rather than declared: `useHover` is
 * `@mantine/hooks`' documented answer and is already a pinned dependency. Keyboard focus is
 * still marked -- `Anchor` carries Mantine's own focus ring.
 */
function DownloadLink({
  href,
  download,
  title,
  label,
  children,
}: {
  href: string;
  download: string;
  title: string;
  label: string;
  children: ReactNode;
}) {
  const runtime = nativeRuntime();
  const { hovered, ref } = useHover<HTMLAnchorElement>();
  return (
    <Anchor
      ref={ref}
      // A test hook, not a style: `StreamList.test.tsx` has no other handle on these links.
      className="row__download"
      flex="none"
      p={4}
      lh={1}
      c={hovered ? "var(--mantine-color-text)" : "dimmed"}
      opacity={hovered ? 1 : 0.7}
      underline="never"
      href={href}
      download={download}
      title={title}
      aria-label={label}
      onClick={(event) => void handleNativeLink(event, runtime, (native) => native.saveFile(href))}
      style={{
        borderRadius: "var(--mantine-radius-sm)",
        pointerEvents: "auto",
        background: hovered ? alpha(token.white, 0.05) : undefined,
      }}
    >
      {children}
    </Anchor>
  );
}

function MetadataItem({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <Box
      component="span"
      title={title}
      display="inline-flex"
      miw={0}
      maw="100%"
      px={8}
      style={{
        alignItems: "center",
        gap: 5,
        borderInlineStart: "1px solid var(--mantine-color-default-border)",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Box>
  );
}

function DurationBadge({ seconds, live = false }: { seconds: number; live?: boolean }) {
  const { t } = useTranslation();
  return (
    <Group
      component="span"
      gap={5}
      wrap="nowrap"
      px={6}
      py={2}
      c={live ? token.recording : undefined}
      title={live ? t("badges.live") : undefined}
      style={{
        border: `1px solid ${live ? alpha(token.recording, 0.4) : "var(--mantine-color-default-border)"}`,
        borderRadius: "var(--mantine-radius-sm)",
        background: live ? alpha(token.recording, 0.09) : undefined,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {live ? (
        <Box
          component="span"
          aria-hidden
          w={9}
          h={9}
          style={{ borderRadius: "50%", background: "currentColor", flexShrink: 0 }}
        />
      ) : (
        <IconStopwatch size={13} stroke={1.5} aria-hidden style={{ flexShrink: 0 }} />
      )}
      <Text component="span" inherit>
        {formatDuration(seconds, "units")}
      </Text>
    </Group>
  );
}

function LiveDuration({ segment }: { segment: Segment }) {
  const now = useNow(1000);
  const seconds =
    (segment.join_seconds ?? segment.seconds) + Math.max(0, (now - Date.parse(segment.end)) / 1000);
  return <DurationBadge seconds={seconds} live />;
}

function Row({
  segment,
  first,
  selected,
  chat,
  signedIn,
  defaultCover,
  now,
}: {
  segment: Segment;
  /** Every row but the first draws the divider above itself. */
  first: boolean;
  selected: boolean;
  chat: boolean;
  signedIn: boolean;
  defaultCover: string | null;
  /**
   * The instant the whole listing is dated against, handed down rather than read here: fifty
   * rows each reading the clock is fifty answers, and a column of relative times that
   * disagree by a minute is the one thing it must not be.
   */
  now: number;
}) {
  const { t } = useTranslation();
  const runtime = nativeRuntime();
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const { hovered, ref: hoverRef } = useHover<HTMLButtonElement>();
  const zone = useZone();
  const startMs = Date.parse(segment.start);
  const title = segment.title ?? segment.raw_title ?? t("streamList.untitled");
  // Only an archived show has files to link at: a buffer row's audio is still being aged
  // out, and a jukebox track has no transcript and no file of its own on disk. Keeping a
  // copy is a member's, so signed out the row offers neither -- it still plays for anyone.
  const downloads = signedIn && segment.kind === "show" && segment.audio_state === "archived";

  return (
    // The row itself lays out artwork, text and badges; the play control is an overlay
    // filling it, drawn underneath so the download link beside the title can take its own
    // clicks.
    <Box
      component="li"
      // Likewise a test hook: the row and its badge strip are what the suite counts and reads.
      className="row"
      pos="relative"
      display="flex"
      px={12}
      py={8}
      // Not listened to, said by the row itself rather than by a badge -- a wash reads at a
      // glance in a way a fifty-row column of words does not. 0 is signed in and has played
      // none of it; null is signed out, which is not a claim about this show at all and must
      // not wash the whole list violet. Selection is tested first because it marks one row and
      // has to tint over the unheard wash, which is what the old source order bought.
      bg={selected ? "accentDim" : segment.listened_pct === 0 ? "unheardDim" : undefined}
      style={{
        alignItems: "center",
        gap: 12,
        borderTop: first ? undefined : "1px solid var(--mantine-color-default-border)",
        // The second signal: the selected wash and the unheard wash are only 1.08:1 apart in
        // luminance over the card, which is not a difference anyone can name. Hard-edged and
        // zero blur, and inset so it costs no layout.
        boxShadow: selected ? "inset 3px 0 0 var(--mantine-color-text)" : undefined,
      }}
    >
      {/* An overlay under the row rather than a wrapper around it: a link may not live
          inside a button, and the download icon has to stand beside the title. Everything
          drawn over it is click-through, so the whole row still plays. */}
      <UnstyledButton
        ref={hoverRef}
        type="button"
        pos="absolute"
        // A translucent wash rather than `--mantine-color-default-hover`, which is opaque and
        // would hide the selected and unheard tints under it. `UnstyledButton` paints nothing
        // and has no hover hook, so the state is read instead of declared.
        style={{
          inset: 0,
          background: hovered ? alpha(token.white, 0.05) : undefined,
        }}
        aria-label={t("streamList.playAria", { title })}
        onClick={() => {
          const state = usePlayer.getState();
          state.select(segment.id);
          // A tombstoned entry is handed over like any other. The player's own loader is
          // what refuses it and says why, and routing it there is what makes the pane name
          // this row rather than whatever played last.
          state.play({ kind: "segment", segment });
          state.setFollow(true);
        }}
      />
      <Artwork
        src={segment.artwork_url ?? defaultCover}
        seed={title}
        size={48}
        dimmed={!segment.playable}
      />
      {/* Relative and click-through: `position` lifts the row's content over the play
          overlay, and `pointer-events: none` hands the clicks back to it, so clicking the
          title still plays the row. The cover above needs neither -- it is unpositioned, so
          the overlay already paints and hit-tests over it. */}
      <Stack gap={0} flex={1} miw={0} pos="relative" style={{ pointerEvents: "none" }}>
        <Group gap={4} wrap="nowrap" miw={0}>
          <Text
            component="span"
            c={segment.playable ? undefined : "dimmed"}
            style={{
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Text>
          {downloads && (
            <DownloadLink
              href={segment.download_path}
              download={`${segment.download_slug}.mp3`}
              title={t("streamList.downloadMp3")}
              label={t("streamList.downloadMp3Aria", { title })}
            >
              <IconDownload size={15} stroke={1.5} style={{ display: "block" }} />
            </DownloadLink>
          )}
          {downloads && runtime && (
            <DownloadButton
              runtime={runtime}
              segmentId={segment.id}
              title={title}
              onError={setDownloadError}
            />
          )}
          {runtime && segment.kind === "show" && segment.audio_state !== "archived" && (
            <Text component="span" size="xs" c="dimmed">
              {t("downloads.onlineOnly")}
            </Text>
          )}
        </Group>
        <Box className="row__metadata" mt={3} fz={12} c="dimmed" style={{ overflow: "hidden" }}>
          {/* Clip the first divider on each wrapped line, keeping separators between items. */}
          <Group gap={0} style={{ marginInlineStart: -9, rowGap: 4 }}>
            {(segment.djs || segment.artist) && (
              <MetadataItem>
                <IconUser size={13} stroke={1.5} aria-hidden style={{ flexShrink: 0 }} />
                <Text component="span" inherit c="text" truncate>
                  {segment.djs ?? segment.artist}
                  {segment.guests && ` ${t("streamList.featuring", { guests: segment.guests })}`}
                </Text>
              </MetadataItem>
            )}
            {place(segment) && (
              <MetadataItem>
                <IconMapPin size={13} stroke={1.5} aria-hidden style={{ flexShrink: 0 }} />
                <Text component="span" inherit truncate>
                  {place(segment)}
                </Text>
              </MetadataItem>
            )}
            <MetadataItem
              title={`${formatDate(startMs, zone.name)} ${formatTime(startMs, zone.name)}`}
            >
              <IconClock size={13} stroke={1.5} aria-hidden style={{ flexShrink: 0 }} />
              <Text component="span" inherit>
                {formatRelative(startMs, now, t)}
              </Text>
            </MetadataItem>
            <MetadataItem>
              {segment.kind === "show" && segment.on_air ? (
                <LiveDuration segment={segment} />
              ) : (
                <DurationBadge seconds={segment.join_seconds ?? segment.seconds} />
              )}
            </MetadataItem>
          </Group>
        </Box>
        {downloads && runtime && (
          <FeedbackLine role="alert" size="xs">
            {downloadError}
          </FeedbackLine>
        )}
      </Stack>
      {/* One letter each, colour-coded, as the review queue's rows are: spelled-out words
          pushed the title out of the way, and a row exists to be scanned. The `title`
          carries the long form. "Unverified" is gone from here entirely -- boundaries a
          reviewer has not signed off on are an editor's business, and the timeline block
          and its legend still say it for anyone who wants it. */}
      <Box
        component="span"
        className="row__badges"
        display="flex"
        flex="none"
        pos="relative"
        style={{ gap: 6, pointerEvents: "none" }}
      >
        {/* "Jukebox", not "track": it names where the entry came from, which is the
            distinction the filter chips draw too. */}
        {segment.kind === "track" && <Badge of="track" />}
        {segment.kind === "show" && segment.on_air && <Badge of="live" />}
        {/* Partly and fully heard only. Nothing at 0%, where the row's violet wash has
            already said it, and nothing when signed out, where there is nothing to say. */}
        {segment.kind === "show" &&
          !!segment.listened_pct &&
          (segment.listened_pct === 100 ? (
            <Badge of="heard" />
          ) : (
            <Badge
              of="partial"
              mark={`${segment.listened_pct}%`}
              hint={t("streamList.partialListenedPct", { pct: segment.listened_pct })}
            />
          ))}
        {segment.starred && <Badge of="starred" />}
        {!segment.playable && <Badge of="expired" />}
      </Box>
      {downloads && chat && (
        <Box
          component="span"
          display="flex"
          flex="none"
          pos="relative"
          style={{ gap: 2, pointerEvents: "none" }}
        >
          <DownloadLink
            href={`/download/${segment.id}/${segment.download_slug}.chat.json`}
            download={`${segment.download_slug}.chat.json`}
            title={t("streamList.downloadChat")}
            label={t("streamList.downloadChatAria", { title })}
          >
            <IconMessage size={15} stroke={1.5} style={{ display: "block" }} />
          </DownloadLink>
        </Box>
      )}
    </Box>
  );
}
